import React, { useState, useEffect } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckIcon from "@mui/icons-material/Check";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { getRightworkAPI } from "../requests/utilities/requests";
import { API_get_quiz_attempt_detail } from "../../../constants";

function QuizReviewModal(props) {
  const t = (english, spanish, chinese) => {
    if (props.language === "chinese") return chinese;
    if (props.language === "spanish") return spanish;
    return english;
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    height: "100%",
    bgcolor: "background.paper",
    border: "2px solid #E2E8F0",
    boxShadow: 24,
    overflowY: "auto",
  };

  const [isLoading, setIsLoading] = useState(true);
  const [attemptData, setAttemptData] = useState(null);

  const modalRef = React.useRef(null);

  const fetchAttemptDetail = async () => {
    setIsLoading(true);
    const api_params = {
      company_id: props.homeCompany,
      attempt_uuid: props.attemptUuid,
      language: props.language,
    };

    try {
      const res = await getRightworkAPI(
        API_get_quiz_attempt_detail,
        api_params
      );
      if (res.status === 200) {
        setAttemptData(res.data.redux?.payload?.quiz_attempt);
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error fetching quiz attempt:", error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (props.open && props.attemptUuid) {
      fetchAttemptDetail();
    }
  }, [props.open, props.attemptUuid]);

  const renderQuestionReview = (response, index) => {
    const {
      question_text,
      question_text_spanish,
      question_text_chinese,
      question_image,
      answer_choices,
      selected_answer_indices,
      correct_answer_indices,
      is_correct
    } = response;

    const questionText =
      props.language === "chinese" && question_text_chinese
        ? question_text_chinese
        : props.language === "spanish" && question_text_spanish
          ? question_text_spanish
          : question_text;

    // Check if correct answers are available (show_correct_answers = true)
    const showCorrectAnswers = correct_answer_indices !== null;

    return (
      <div key={index} className="mb-8 pb-8 border-b border-slate-200 last:border-b-0">
        {/* Question Header */}
        <div className="flex items-start gap-3 mb-4">
          {showCorrectAnswers && (
            <div className="flex-shrink-0 mt-1">
              {is_correct ? (
                <CheckCircleIcon className="text-emerald-600" style={{ fontSize: "24px" }} />
              ) : (
                <CancelIcon className="text-rose-600" style={{ fontSize: "24px" }} />
              )}
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-slate-500">
                {t("Question", "Pregunta", "题目")} {index + 1}
              </span>
              {showCorrectAnswers && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  is_correct
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-rose-100 text-rose-700'
                }`}>
                  {is_correct
                    ? t("Correct", "Correcta", "正确")
                    : t("Incorrect", "Incorrecta", "错误")
                  }
                </span>
              )}
            </div>
            <h4 className="text-lg text-slate-900">{questionText}</h4>
            {question_image && (
              <div className="mt-3">
                <img
                  src={question_image.url}
                  alt="Question"
                  className="rounded-lg max-h-64 object-cover"
                />
              </div>
            )}
          </div>
        </div>

        {/* Answer Choices */}
        <div className={showCorrectAnswers ? "ml-9 space-y-2" : "space-y-2"}>
          {answer_choices.map((choice, choiceIndex) => {
            const isSelected = selected_answer_indices.includes(choiceIndex);
            const isCorrect = showCorrectAnswers ? correct_answer_indices.includes(choiceIndex) : false;

            let bgColor = "bg-slate-50";
            let textColor = "text-slate-700";
            let borderColor = "border-slate-200";

            if (showCorrectAnswers) {
              // Show correct/incorrect colors when answers are revealed
              if (isSelected && isCorrect) {
                // User selected correct answer
                bgColor = "bg-emerald-50";
                textColor = "text-emerald-900";
                borderColor = "border-emerald-300";
              } else if (isSelected && !isCorrect) {
                // User selected wrong answer
                bgColor = "bg-rose-50";
                textColor = "text-rose-900";
                borderColor = "border-rose-300";
              } else if (!isSelected && isCorrect) {
                // Correct answer that user didn't select
                bgColor = "bg-emerald-50";
                textColor = "text-emerald-900";
                borderColor = "border-emerald-300";
              }
            } else {
              // Neutral view - only highlight what user selected
              if (isSelected) {
                bgColor = "bg-violet-50";
                textColor = "text-violet-900";
                borderColor = "border-violet-300";
              }
            }

            return (
              <div
                key={choiceIndex}
                className={`p-3 rounded-lg border ${bgColor} ${borderColor} flex items-start gap-3`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  {showCorrectAnswers ? (
                    // Show correct/incorrect indicators when answers are revealed
                    isCorrect ? (
                      <div className="w-5 h-5 rounded-full bg-emerald-600 flex items-center justify-center">
                        <CheckIcon style={{ fontSize: "16px", color: "white" }} />
                      </div>
                    ) : isSelected ? (
                      <div className="w-5 h-5 rounded-full bg-rose-600 flex items-center justify-center">
                        <CloseIcon style={{ fontSize: "16px", color: "white" }} />
                      </div>
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-slate-300 bg-white"></div>
                    )
                  ) : (
                    // Neutral view - just show selection state
                    isSelected ? (
                      <div className="w-5 h-5 rounded-full bg-violet-600 flex items-center justify-center">
                        <CheckIcon style={{ fontSize: "16px", color: "white" }} />
                      </div>
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-slate-300 bg-white"></div>
                    )
                  )}
                </div>
                <div className="flex-1">
                  <p className={textColor}>{choice.text}</p>
                  {choice.file_url && (
                    <div className="mt-2">
                      <img
                        src={choice.file_url}
                        alt="Answer choice"
                        className="rounded-lg max-h-48 object-cover"
                      />
                    </div>
                  )}
                </div>
                <div className="flex-shrink-0">
                  {isSelected && (
                    <span className="text-xs px-2 py-1 rounded bg-violet-100 text-violet-700">
                      {t("Your answer", "Tu respuesta", "你的答案")}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Modal
      open={props.open}
      onClose={props.handleClose}
    >
      <Box sx={style} ref={modalRef}>
        {!isLoading && attemptData ? (
          <div className="w-full h-full flex flex-col">
            {/* Header */}
            <div className="py-5 px-6 w-full border-b border-slate-200 bg-white sticky top-0 z-10">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-slate-900">
                    {t("Quiz Review", "Revisión del cuestionario", "测验回顾")}
                  </h2>
                  <p className="text-sm text-slate-500 mt-1">{attemptData.quiz_name}</p>
                  {attemptData.question_responses?.[0]?.correct_answer_indices === null && (
                    <p className="text-xs text-slate-400 mt-1 italic">
                      {props.language === "spanish"
                        ? "Mostrando tus respuestas solamente"
                        : props.language === "chinese"
                          ? "仅显示你的答案"
                          : "Showing your answers only"}
                    </p>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm text-slate-500">
                    {t("Score", "Puntuación", "分数")}
                  </p>
                  <p className={`text-2xl font-bold ${
                    attemptData.is_passed ? 'text-emerald-600' : 'text-rose-600'
                  }`}>
                    {parseFloat(attemptData.score).toFixed(0)}%
                  </p>
                </div>
                {/* Close Button */}
                <button
                  className="flex-shrink-0 w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
                  onClick={props.handleClose}
                >
                  <CloseIcon />
                </button>
              </div>
            </div>

            {/* Questions Review */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="max-w-3xl mx-auto">
                {attemptData.question_responses.map((response, index) =>
                  renderQuestionReview(response, index)
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-slate-200 bg-slate-50">
              <div className="max-w-3xl mx-auto">
                <button
                  className="w-full px-6 py-3 rounded-lg bg-violet-700 text-white text-sm font-medium hover:bg-violet-800"
                  onClick={props.handleClose}
                >
                  {t("Close", "Cerrar", "关闭")}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full h-full relative">
            {/* Close Button for Loading State */}
            <button
              className="absolute right-4 top-4 w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors z-10"
              onClick={props.handleClose}
            >
              <CloseIcon />
            </button>
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <LoadSpinner />
            </div>
          </div>
        )}
      </Box>
    </Modal>
  );
}

export default QuizReviewModal;
